import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, switchMap } from 'rxjs';
import { SalesInsightsFilterValues } from '../../models/sales-insights.model';
import { SalesInsightsActivityService } from '../../services/sales-insights-activity.service';
import {
  SalesInsightsStatTile,
  SalesInsightsTileSummary,
  salesInsightsStatTiles
} from '../../utils/sales-insights-tiles';
import { salesInsightsColor } from '../../utils/sales-insights-theme';
import { SalesInsightsBaseComponent } from '../sales-insights-base/sales-insights-base.component';

export interface SalesInsightsStatTileView extends SalesInsightsStatTile {
  // The palette colour for neutral and per-type tiles; the tone colours come from the stylesheet.
  rgb: string | null;
  ring: boolean;
}

const RING_RADIUS = 16;
const SKELETON_TILES = [0, 1, 2, 3, 4];

@Component({
  selector: 'lib-sales-insights-stat-tiles',
  standalone: false,
  templateUrl: './sales-insights-stat-tiles.component.html',
  styleUrl: './sales-insights-stat-tiles.component.scss'
})
export class SalesInsightsStatTilesComponent
  extends SalesInsightsBaseComponent<SalesInsightsTileSummary>
  implements OnInit
{
  protected activityService = inject(SalesInsightsActivityService);
  private destroy = inject(DestroyRef);

  readonly ringCircumference = 2 * Math.PI * RING_RADIUS;
  readonly ringRadius = RING_RADIUS;
  readonly skeletonTiles = SKELETON_TILES;

  tiles: SalesInsightsStatTileView[] = [];
  activeFlag: string | null = null;

  ngOnInit(): void {
    super.ngOnInit();
    this.dashboardState.customerHealthFlag$
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe(flag => (this.activeFlag = flag));
  }

  get filterable(): boolean {
    return (
      this.options.filter !== false &&
      this.config?.source?.endpoint === 'customer-health/summary' &&
      this.dashboardState.hasCustomerList
    );
  }

  select(tile: SalesInsightsStatTile): void {
    if (!this.filterable || !tile.flag) {
      return;
    }
    this.dashboardState.setCustomerHealthFlag(this.activeFlag === tile.flag ? null : tile.flag);
  }

  ringOffset(tile: SalesInsightsStatTile): number {
    return this.ringCircumference * (1 - Math.max(0, Math.min(100, tile.progress || 0)) / 100);
  }

  protected isEmpty(data: SalesInsightsTileSummary | null): boolean {
    return !data || !this.tiles.length;
  }

  protected onData(data: SalesInsightsTileSummary | null): void {
    const ring = this.options.progress !== 'bar';
    const icons: { [key: string]: string } = this.options.tileIcons || {};
    const colors: { [key: string]: number } = this.options.tileColors || {};
    this.tiles = salesInsightsStatTiles(this.options.tiles, data, type => this.activityService.typeName(type)).map(
      tile => {
        const icon = icons[tile.key] ? this.withStyle(icons[tile.key]) : tile.icon;
        if (tile.type) {
          return {
            ...tile,
            icon: icons[tile.key] ? icon : this.activityService.typeIcon(tile.type, this.options.typeIcons),
            rgb: salesInsightsColor(
              colors[tile.key] || this.activityService.typeColorIndex(tile.type, this.options.typeColors)
            ),
            ring
          };
        }
        const rgb = colors[tile.key]
          ? salesInsightsColor(colors[tile.key])
          : tile.tone === 'neutral'
            ? this.colorRgb
            : null;
        return { ...tile, icon, rgb, ring };
      }
    );
  }

  // Per-type tiles take their colour and icon from the type settings, so those are read before the numbers.
  protected fetch(filters: SalesInsightsFilterValues): Observable<SalesInsightsTileSummary> {
    if (!String(this.config?.source?.endpoint || '').startsWith('activities')) {
      return super.fetch(filters);
    }
    return this.activityService.loadTypes().pipe(switchMap(() => super.fetch(filters)));
  }

  protected reloads(): Observable<unknown> {
    return this.dashboardState.changes('activities', 'goals', 'activityTypes');
  }

  private withStyle(icon: string): string {
    return /\bfa-(solid|regular|light|thin|duotone|brands)\b/.test(icon) ? icon : `fa-solid ${icon}`;
  }
}
